import { MenuAvailable } from '@/utils/drupal-api/__generated__/graphql';
import { BrandConfig, ClientBrandConfig } from 'types/brand-config';

import {
  BVR_FILTERS,
  MRR_FEATURED_CATEGORIES,
  MRR_FILTERS,
} from './filters/brand-filters';

// Define your branding configurations for different domains here
export const brands: Record<string, BrandConfig> = {
  mrr: {
    key: 'mrr',
    basicAuth: {
      user: 'mrr',
      pass: 'mauialoha',
    },
    title: 'Maui Resort Rentals',
    subtitle: '',
    companyName: 'Maui Resort Rentals, Inc.',
    brokerLicense: '',
    description: '',
    greeting: 'Aloha',
    thanksGreeting: 'Mahalo',
    brandId: '49017bd8-7ce6-40bd-952e-0b4a1429b19b',
    appleIconUrl: '/mrr/assets/apple-touch-icon.png',
    logoUrl: '/mrr/assets/logo.svg',
    iconUrl: '/mrr/assets/geo-icon.svg',
    favIconUrl: '/mrr/assets/favicon-16x16.png',
    shortcutIconUrl: '/mrr/assets/favicon-16x16.png',
    manifestUrl: '/mrr/assets/site.webmanifest',
    customCssUrl: '/mrr/style.css',
    cloudflareZoneId: '59b7c9bb2ad3e8b0b750dc6ce75b4ab8',
    domain: 'mauiresortrentals.com',
    email: 'experience@mauiresortrentals.com',
    locale: 'Maui',
    address: {
      streetAddress: '10 Halawai Dr #A-3',
      city: 'Lahaina',
      state: 'HI',
      postalCode: '96761',
    },
    timezone: 'Pacific/Honolulu',
    themeSettings: {
      themeColorPresets: 'default',
    },
    phone: '+18086626284',
    phoneDisplay: '(808) 662-6284',
    phoneTollFree: '+18558086284',
    phoneTollFreeDisplay: '(855) 808-6284',
    sendGrid: {
      clientUrl: 'https://mauiresortrentals.com',
      fromEmail: 'Maui Resort Rentals <noreply@mauiresortrentals.com>',
      templateIds: {
        otp: 'd-dc7a77974dd14944934f0d648322c386',
      },
    },
    cmsId: 'maui_resort_rentals',
    managerIds: process.env.MRR_MANAGER_IDS
      ? process.env.MRR_MANAGER_IDS.split(',').map((id) => id.trim())
      : ['0013000001G2nu6AAB'], // default to Prod
    paths: {
      booking: '/book',
      listing: '/rental/',
      listings: '/resort/all',
      resort: '/resort/',
      resorts: '/resorts',
      blog: '/blog',
      listing404: '/',
    },
    newListingDays: 90,
    menus: {
      main: MenuAvailable.Mrrmain,
      footer: MenuAvailable.Mrrfooter,
    },
    canonicalHostname: 'www.mauiresortrentals.com',
    hostnames: ['mauiresortrentals.com', 'mrr-local'],
    termsAndConditions: '/terms-and-conditions',
    privacyPolicy: '/privacy-policy',
    gtm: {
      tagManagerId: 'GTM-NND5WX8',
    },
    resortLabel: 'Resort',
    resortPluralLabel: 'Resorts',
    filterCategories: MRR_FILTERS,
    featuredCategories: MRR_FEATURED_CATEGORIES,
    socialLinks: {
      facebook: 'https://www.facebook.com/mauiresortrentals',
      twitter: 'https://twitter.com/Maui_Resort',
      instagram: 'https://www.instagram.com/mauiresortrentals/',
      pinterest: 'https://www.pinterest.com/mauireality/',
      youtube: 'https://www.youtube.com/channel/UCyzYfwnza00Nk-qB9kzdLCw',
    },
    showPriceRanges: true,
    showResorts: true,
    showResortInSearchResults: true,
    showRoomNotes: false,
    showListingView: true,
    showMap: false,
    defaultMapLayer: 'roadmap',
    maxMapZoom: undefined,
    defaultMapCamera: { center: { lat: 20.92, lng: -156.68 }, zoom: 11 }, // West Maui
    disableMap: false,
    showPetFriendly: false,
    showAcStatus: false,
    showPropertyListOnSearch: false,
    showResortOnSearch: true,
    showFiltersOnHomeSearch: false,
    travelInsuranceEnabled: true,
    formRecipients: {
      catchAll: ['devops@mauiresortrentals.com'],
      emailSignup: ['email-signup@mauiresortrentals.com'],
      general: ['general-inquiry@mauiresortrentals.com'],
      propertyManagement: ['management-inquiry@mauiresortrentals.com'],
      realty: ['real-estate-inquiry@mauiresortrentals.com'],
      employment: ['employment-inquiry@mauiresortrentals.com'],
    },
    stripePublishableKey: process.env
      .NEXT_PUBLIC_MRR_STRIPE_PUBLISHABLE_KEY as string,
    paymentMethods: [
      { type: 'card' },
      { type: 'us_bank_account' },
      { type: 'affirm', min: 50, max: 30000 },
    ],
    mailchimp: {
      dc: 'us17',
      apiKeyEnvVar: 'MRR_MAILCHIMP_API_KEY',
      signUpListId: '750124bea5',
    },
    minStay: 2,
    maxStay: 180,
    bookingFee: {
      title: 'Guest Assurance Fee',
      description: 'Provides for payment processing and guest verification.',
    },
    mobileMapKey: process.env.NEXT_PUBLIC_MAP_KEY_MOBILE_MRR || 'mrrmobile',
    desktopMapKey: process.env.NEXT_PUBLIC_MAP_KEY_DESKTOP_MRR || 'mrrdesktop',
    fullPaymentDiscountAvailable: true,
    allowBookingPastMaxStay: false,
    personalizationRealm: 'custom-maui-resort-rentals',
    guestPortalUrl: 'https://guest.mauiresortrentals.com',
  },
  bvr: {
    key: 'bvr',
    title: 'Beachview Rentals',
    subtitle: 'Newport Beach Vacation Rental',
    companyName: 'Beachview Rentals',
    brokerLicense: 'DRE #02247400',
    description: '',
    basicAuth: {
      user: 'bvr',
      pass: 'balboa',
    },
    greeting: 'Hello',
    thanksGreeting: 'Thank you',
    brandId: '55d26c7f-eb8d-4f82-937b-8b4c3ca7c709',
    appleIconUrl: '/bvr/assets/apple-touch-icon.png',
    logoUrl: '/bvr/assets/BVR_DR_Color_Horizontal.svg',
    iconUrl: '/bvr/assets/geo-icon.svg',
    favIconUrl: '/bvr/assets/favicon-32x32.png',
    shortcutIconUrl: '/bvr/assets/favicon-16x16.png',
    manifestUrl: '/bvr/assets/site.webmanifest',
    customCssUrl: '/bvr/style.css',
    cloudflareZoneId: '64e80916d2d2c0e1a3ccfa1202f4b473',
    domain: 'beachviewrentals.com',
    email: 'info@beachviewrentals.com',
    locale: 'Newport Beach',
    gtm: {
      tagManagerId: 'GTM-N4J46HHZ',
    },
    address: {
      streetAddress: '2210 Newport Blvd Suite 200',
      city: 'Newport Beach',
      state: 'CA',
      postalCode: '92663',
    },
    timezone: 'America/Los_Angeles',
    themeSettings: {
      themeColorPresets: 'default',
    },
    bookingFee: {
      title: 'Guest Assurance Fee',
      description: 'Provides for payment processing and guest verification.',
    },
    phone: '+19496732627',
    phoneDisplay: '(949) 673-2627',
    sendGrid: {
      clientUrl: 'https://beachviewrentals.com',
      fromEmail: 'Beachview Rentals <noreply@beachviewrentals.com>',
      templateIds: {
        otp: 'd-5eb676878be44ddea65c527cb999a4e7',
      },
    },
    cmsId: 'beachview_rentals',
    managerIds: process.env.BVR_MANAGER_IDS
      ? process.env.BVR_MANAGER_IDS.split(',').map((id) => id.trim())
      : ['001PZ00000BICZdYAP'], // default to Prod
    paths: {
      booking: '/book',
      listing: '/vacation-rentals/',
      listings:
        '/newport-beach-vacation-rentals/search-newport-beach-vacation-homes',
      blog: '/beachview-blog',
      listing404: '/newport-beach-vacation-rentals',
    },
    resortLabel: 'Area',
    resortPluralLabel: 'Areas',
    siteMapLabels: {
      nameColumn: 'Street Address',
      searchPlaceholder: 'Search by Street Address',
    },
    newListingDays: 180,
    menus: {
      main: MenuAvailable.Bvrmain,
      footer: MenuAvailable.Bvrfooter,
    },
    canonicalHostname: 'www.beachviewrentals.com',
    hostnames: [
      'beachviewrealty.com',
      'beachviewrentals.com',
      'bvr-local',
      'remote-ned',
    ],
    termsAndConditions: '/additional-information/terms-and-conditions',
    privacyPolicy: '/additional-information/privacy-policy',
    filterCategories: BVR_FILTERS,
    // featuredCategories: [],
    socialLinks: {
      facebook: 'https://www.facebook.com/beachviewrealtyNB/',
      instagram:
        'https://www.instagram.com/beachviewrentals?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==',
      linkedin:
        'https://www.linkedin.com/company/dream-resorts/about/?viewAsMember=true',
    },
    showPriceRanges: false,
    showResorts: false,
    showResortInSearchResults: true,
    showRoomNotes: true,
    showMap: true,
    defaultMapLayer: 'satellite',
    maxMapZoom: undefined,
    defaultMapCamera: { center: { lat: 33.61, lng: -117.9 }, zoom: 12 }, // Newport Beach
    disableMap: false,
    showListingView: false,
    showPetFriendly: true,
    showAcStatus: true,
    showPropertyListOnSearch: false,
    showResortOnSearch: false,
    showFiltersOnHomeSearch: true,
    travelInsuranceEnabled: true,
    formRecipients: {
      catchAll: ['info@beachviewrentals.com'],
      general: ['info@beachviewrentals.com'],
      employment: ['careers@beachviewrentals.com'],
      propertyManagement: ['management-inquiry@beachviewrentals.com'],
    },
    stripePublishableKey: process.env
      .NEXT_PUBLIC_BVR_STRIPE_PUBLISHABLE_KEY as string,
    paymentMethods: [
      { type: 'card' },
      { type: 'us_bank_account' },
      { type: 'affirm', min: 50, max: 30000 },
    ],
    mailchimp: {
      dc: 'us17',
      apiKeyEnvVar: 'MRR_MAILCHIMP_API_KEY',
      signUpListId: '2dfe36a254',
    },
    minStay: 2,
    maxStay: 180,
    checkfront: {
      path: '/beach-and-baby-gear-rentals',
      title: 'Beach and Baby Gear Rentals',
      url: 'https://beachviewbikes.checkfront.com/reserve/',
      params: {
        inline: '1',
        category_id: '4,11',
        options: 'tabs',
        provider: 'droplet',
        ssl: '1',
        src: 'https://www.beachviewrentals.com',
      },
      trailing: '1733769361848',
    },
    mobileMapKey: process.env.NEXT_PUBLIC_MAP_KEY_MOBILE_BVR || 'bvrmobile',
    desktopMapKey: process.env.NEXT_PUBLIC_MAP_KEY_DESKTOP_BVR || 'bvrdesktop',
    fullPaymentDiscountAvailable: false,
    allowBookingPastMaxStay: true,
    guestPortalUrl: 'https://guest.beachviewrentals.com',
  },
  drs: {
    key: 'drs',
    basicAuth: {
      user: 'drs',
      pass: 'dreambig',
    },
    title: 'Dream Resorts',
    subtitle: '',
    companyName: 'Dream Resorts',
    brokerLicense: '',
    description: '',
    greeting: 'Aloha',
    thanksGreeting: 'Mahalo',
    brandId: 'N/A',
    appleIconUrl: '/drs/assets/DR_Logo_Monogram_Light.png',
    logoUrl: '/drs/assets/dream-resorts-logo-7.svg',
    iconUrl: '/drs/assets/dream-resorts-icon-3.svg',
    favIconUrl: '/drs/assets/DR_Logo_Monogram_Light.png',
    shortcutIconUrl: '/drs/assets/DR_Logo_Monogram_Light.png',
    manifestUrl: '/drs/assets/site.webmanifest',
    customCssUrl: '/drs/style.css',
    cloudflareZoneId: '54f6301138d8a778e4a7d55cd055892e',
    domain: 'dreamresorts.com',
    email: 'info@dreamresorts.com',
    locale: 'US',
    address: {
      streetAddress: '2210 Newport Blvd Suite 200',
      city: 'Newport Beach',
      state: 'CA',
      postalCode: '92663',
    },
    timezone: 'America/Los_Angeles',
    themeSettings: {
      themeColorPresets: 'default',
    },
    phone: '+19495206903',
    phoneDisplay: '(949) 520-6903',
    sendGrid: {
      clientUrl: 'https://www.dreamresorts.com',
      fromEmail: 'Dream Resorts <noreply@dreamresorts.com>',
      templateIds: {
        otp: 'N/A',
      },
    },
    cmsId: 'dream_resorts',
    managerIds: [],
    paths: {
      // booking: '/book',
      // listing: '/rental/',
      // listings: '/resort/all',
      // resort: '/resort/',
      // resorts: '/resorts',
      blog: '/dream-resorts-blog',
      listing404: '/',
    },
    newListingDays: 90,
    menus: {
      main: MenuAvailable.Drsmain,
      footer: MenuAvailable.Drsfooter,
    },
    canonicalHostname: 'www.dreamresorts.com',
    hostnames: ['dreamresorts.com', 'drs-local'],
    termsAndConditions: '/policies/terms-and-conditions',
    privacyPolicy: '/policies/privacy-policy',
    gtm: {
      tagManagerId: 'GTM-KKXT73LK',
    },
    resortLabel: 'Resort',
    resortPluralLabel: 'Resorts',
    socialLinks: {
      facebook: 'https://www.facebook.com/profile.php?id=61574963900414',
      instagram: 'https://www.instagram.com/staydreamresorts',
      linkedin:
        'https://www.linkedin.com/company/dream-resorts/about/?viewAsMember=true',
    },
    showPriceRanges: true,
    showResorts: false,
    showResortInSearchResults: true,
    showRoomNotes: false,
    showListingView: true,
    showMap: false,
    defaultMapLayer: 'roadmap',
    maxMapZoom: undefined,
    defaultMapCamera: { center: { lat: 0, lng: 0 }, zoom: 2 },
    disableMap: false,
    showPetFriendly: false,
    showAcStatus: false,
    showPropertyListOnSearch: false,
    showResortOnSearch: true,
    showFiltersOnHomeSearch: false,
    travelInsuranceEnabled: true,
    formRecipients: {
      catchAll: ['info@dreamresorts.com'],
      emailSignup: ['email-signup@dreamresorts.com'],
      general: ['general-inquiry@dreamresorts.com'],
      propertyManagement: ['management-inquiry@dreamresorts.com'],
      employment: ['careers@dreamresorts.com'],
    },
    stripePublishableKey: process.env
      .NEXT_PUBLIC_DRS_STRIPE_PUBLISHABLE_KEY as string,
    paymentMethods: [
      { type: 'card' },
      { type: 'us_bank_account' },
      { type: 'affirm', min: 50, max: 30000 },
    ],
    mailchimp: {
      dc: 'us17',
      apiKeyEnvVar: 'MRR_MAILCHIMP_API_KEY', // DRS list is under MRR account
      signUpListId: '2dfe36a254',
    },
    minStay: 2,
    maxStay: 180,
    bookingFee: {
      title: 'Guest Assurance Fee',
      description: 'Provides for payment processing and guest verification.',
    },
    mobileMapKey: process.env.NEXT_PUBLIC_MAP_KEY_MOBILE_MRR || 'mrrmobile',
    desktopMapKey: process.env.NEXT_PUBLIC_MAP_KEY_DESKTOP_MRR || 'mrrdesktop',
    fullPaymentDiscountAvailable: true,
    allowBookingPastMaxStay: false,
  },
  mrs: {
    key: 'mrs',
    basicAuth: {
      user: 'mrs',
      pass: 'kaanapali',
    },
    title: 'Maui Resort Resales',
    subtitle: 'Maui Timeshare Resales',
    companyName: 'Maui Resort Resales',
    brokerLicense: '',
    description:
      "Local upscale timeshare experts specializing in Marriott's Maui Ocean Club, The Hyatt Residence Club, The Westin Kaanapali Ocean Resort, and The Westin Nanea Villas.",
    greeting: 'Aloha',
    thanksGreeting: 'Mahalo',
    brandId: 'N/A',
    appleIconUrl: '/mrs/assets/apple-touch-icon.png',
    logoUrl: '/mrs/assets/logo.svg',
    iconUrl: '/mrs/assets/geo-icon.svg',
    favIconUrl: '/mrs/assets/favicon-32x32.png',
    shortcutIconUrl: '/mrs/assets/favicon-16x16.png',
    manifestUrl: '/mrs/assets/site.webmanifest',
    customCssUrl: '/mrs/style.css',
    domain: 'mauiresortresales.com',
    email: 'info@mauiresortresales.com',
    locale: 'Maui',
    address: {
      streetAddress: "2435 Ka'anapali Pkwy Suite D-6",
      city: 'Lahaina',
      state: 'HI',
      postalCode: '96761',
    },
    timezone: 'Pacific/Honolulu',
    themeSettings: {
      themeColorPresets: 'default',
    },
    phone: '+18085010078',
    phoneDisplay: '(808) 501-0078',
    sendGrid: {
      clientUrl: 'https://www.mauiresortresales.com',
      fromEmail: 'Maui Resort Resales <noreply@mauiresortresales.com>',
      templateIds: {
        otp: 'N/A',
      },
    },
    cmsId: 'maui_resort_resales',
    managerIds: [],
    paths: {
      listing404: '/',
    },
    menus: {
      main: MenuAvailable.Mrsmain,
      footer: MenuAvailable.Mrsfooter,
    },
    canonicalHostname: 'www.mauiresortresales.com',
    hostnames: ['mauiresortresales.com', 'mrs-local'],
    termsAndConditions: '/terms-and-conditions',
    privacyPolicy: '/privacy-policy',
    resortLabel: 'Resort',
    resortPluralLabel: 'Resorts',
    socialLinks: {
      facebook: 'https://facebook.com/mauiresortresales',
      twitter: 'https://twitter.com/mauiresortresales',
      instagram: 'https://instagram.com/mauiresortresales',
      pinterest: 'https://pinterest.com/mauiresortresales',
      youtube: 'https://youtube.com/mauiresortresales',
      linkedin: 'https://linkedin.com/in/mauiresortresales',
    },
    showPriceRanges: false,
    showResorts: false,
    showResortInSearchResults: false,
    showRoomNotes: false,
    showListingView: false,
    showMap: false,
    defaultMapLayer: 'roadmap',
    maxMapZoom: undefined,
    defaultMapCamera: { center: { lat: 20.92, lng: -156.68 }, zoom: 11 }, // West Maui
    disableMap: false,
    showPetFriendly: false,
    showAcStatus: false,
    showPropertyListOnSearch: false,
    showResortOnSearch: false,
    showFiltersOnHomeSearch: false,
    travelInsuranceEnabled: false,
    formRecipients: {
      catchAll: ['info@mauiresortresales.com'],
      general: ['info@mauiresortresales.com'],
      timeshareResale: ['info@mauiresortresales.com'],
    },
    stripePublishableKey: process.env
      .NEXT_PUBLIC_MRS_STRIPE_PUBLISHABLE_KEY as string,
    paymentMethods: [],
    minStay: 2,
    maxStay: 180,
    bookingFee: {
      title: 'Guest Assurance Fee',
      description: 'Provides for payment processing and guest verification.',
    },
    mobileMapKey: process.env.NEXT_PUBLIC_MAP_KEY_MOBILE_MRR || 'mrrmobile',
    desktopMapKey: process.env.NEXT_PUBLIC_MAP_KEY_DESKTOP_MRR || 'mrrdesktop',
    fullPaymentDiscountAvailable: false,
    allowBookingPastMaxStay: false,
  },
};

/**
 * @param hostName url host name
 * @returns a brand config
 */
export const brandConfigByHostname = (hostName: string) => {
  if (!hostName) {
    throw new Error('Hostname is required');
  }
  const brandKey =
    Object.keys(brands).find((key) =>
      brands[key].hostnames.find((entry) =>
        hostName ? hostName.includes(entry) : false
      )
    ) || null;
  if (!brandKey) {
    throw new Error('Brand key not found for hostname: ' + hostName);
  }
  return brands[brandKey];
};

export const brandConfigByKey = (key: string) => {
  if (!brands[key]) {
    throw new Error('Brand not found for key: ' + key);
  }
  return brands[key];
};

const CLIENT_KEYS: (keyof ClientBrandConfig)[] = [
  'title',
  'greeting',
  'thanksGreeting',
  'key',
  'email',
  'description',
  'companyName',
  'themeSettings',
  'timezone',
  'gtm',
  'termsAndConditions',
  'privacyPolicy',
  'logoUrl',
  'phone',
  'phoneDisplay',
  'phoneTollFree',
  'phoneTollFreeDisplay',
  'resortLabel',
  'resortPluralLabel',
  'siteMapLabels',
  'filterCategories',
  'featuredCategories',
  'minStay',
  'maxStay',
  'showMap',
  'defaultMapLayer',
  'maxMapZoom',
  'defaultMapCamera',
  'disableMap',
  'showListingView',
  'showPetFriendly',
  'showAcStatus',
  'showPropertyListOnSearch',
  'showPriceRanges',
  'showResortOnSearch',
  'showFiltersOnHomeSearch',
  'newListingDays',
  'showResortInSearchResults',
  'travelInsuranceEnabled',
  'ignoreShowOnWebsite',
  'stripePublishableKey',
  'paymentMethods',
  'paths',
  'bookingFee',
  'mobileMapKey',
  'desktopMapKey',
  'fullPaymentDiscountAvailable',
  'allowBookingPastMaxStay',
  'brokerLicense',
];

export const clientBrandConfigByKey = (key: string): ClientBrandConfig => {
  const full = brandConfigByKey(key);
  const client = {} as Record<string, unknown>;
  for (const k of CLIENT_KEYS) {
    if (k in full) client[k] = full[k];
  }
  client.hasPersonalization = !!full.personalizationRealm;
  return client as ClientBrandConfig;
};

export const clientBrandConfigByHostname = (hostName: string) => {
  return clientBrandConfigByKey(brandConfigByHostname(hostName).key);
};
